import React, { useState } from 'react';
import { Search, Filter, Download, X, Plus, Trash2 } from 'lucide-react';

const Reports = () => {
  const [selectedForm, setSelectedForm] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Available forms
  const forms = [
    { id: 1, name: 'Test Form Alpha' },
    { id: 2, name: 'Test Form Beta' },
    { id: 3, name: 'Test Form Gamma' },
  ];

  // Form fields based on selected form
  const getFormFields = (formId) => {
    // This would come from your actual form structure
    return [
      { id: 'name', label: 'Name', type: 'text' },
      { id: 'email', label: 'Email', type: 'text' },
      { id: 'district', label: 'District', type: 'dropdown', options: ['Chennai', 'Coimbatore', 'Madurai'] },
      { id: 'role', label: 'Role', type: 'dropdown', options: ['Section Officer', 'Police Inspector', 'DADWO Officer', 'Warden'] },
      { id: 'status', label: 'Status', type: 'dropdown', options: ['Pending', 'Approved', 'Rejected'] },
      { id: 'date', label: 'Submission Date', type: 'date' },
    ];
  };

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
  ];

  const addFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: '',
      operator: 'equals',
      value: ''
    };
    setFilters([...filters, newFilter]);
  };

  const updateFilter = (id, key, value) => {
    setFilters(filters.map(filter => 
      filter.id === id ? { ...filter, [key]: value } : filter
    ));
  };

  const removeFilter = (id) => {
    setFilters(filters.filter(filter => filter.id !== id));
  };

  const handleGenerateReport = () => {
    // Simulate report generation with dummy data
    const dummyData = [
      {
        id: 1,
        name: 'Test User Alpha',
        email: 'alpha@test.com',
        district: 'Chennai',
        role: 'Section Officer',
        status: 'Approved',
        date: '2024-01-15'
      },
      {
        id: 2,
        name: 'Test User Beta',
        email: 'beta@test.com',
        district: 'Coimbatore',
        role: 'Police Inspector',
        status: 'Pending',
        date: '2024-01-14'
      },
      {
        id: 3,
        name: 'Test User Gamma',
        email: 'gamma@test.com',
        district: 'Madurai',
        role: 'DADWO Officer',
        status: 'Approved',
        date: '2024-01-13'
      },
      {
        id: 4,
        name: 'Test User Delta',
        email: 'delta@test.com',
        district: 'Chennai',
        role: 'Warden',
        status: 'Rejected',
        date: '2024-01-12'
      },
      {
        id: 5,
        name: 'Test User Epsilon',
        email: 'epsilon@test.com',
        district: 'Coimbatore',
        role: 'Section Officer',
        status: 'Approved',
        date: '2024-01-11'
      },
    ];

    // Apply filters
    let filteredData = dummyData;
    filters.forEach(filter => {
      if (filter.field && filter.value) {
        filteredData = filteredData.filter(item => {
          const fieldValue = item[filter.field]?.toString().toLowerCase();
          const filterValue = filter.value.toLowerCase();

          switch (filter.operator) {
            case 'equals':
              return fieldValue === filterValue;
            case 'contains':
              return fieldValue?.includes(filterValue);
            case 'not_equals':
              return fieldValue !== filterValue;
            default:
              return true;
          }
        });
      }
    });

    setReportData(filteredData);
    setShowResults(true);
    setShowFilterModal(false);
  };

  const handleExportCSV = () => {
    // Convert data to CSV
    const headers = ['Name', 'Email', 'District', 'Role', 'Status', 'Date'];
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => 
        [row.name, row.email, row.district, row.role, row.status, row.date].join(',')
      )
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'report.csv';
    a.click();
  };

  const handleExportExcel = () => {
    alert('Excel export functionality would be implemented here');
  };

  const formFields = selectedForm ? getFormFields(selectedForm) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
        <p className="text-sm text-gray-600 mt-1">Generate and export form submission reports</p>
      </div>

      {/* Form Selection */}
      <div className="bg-white border border-gray-200 p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Form</label>
            <select
              value={selectedForm}
              onChange={(e) => {
                setSelectedForm(e.target.value);
                setFilters([]);
                setShowResults(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
            >
              <option value="">Choose a form</option>
              {forms.map(form => (
                <option key={form.id} value={form.id}>{form.name}</option>
              ))}
            </select>
          </div>

          {selectedForm && (
            <div className="flex gap-3">
              <button
                onClick={() => setShowFilterModal(true)}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Add Filters ({filters.length})
              </button>
              <button
                onClick={handleGenerateReport}
                className="px-4 py-2 bg-green-600 text-white font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                Generate Report
              </button>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {filters.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Active Filters:</h3>
            <div className="flex flex-wrap gap-2">
              {filters.map(filter => {
                const field = formFields.find(f => f.id === filter.field);
                return (
                  <div key={filter.id} className="bg-blue-50 border border-blue-200 px-3 py-1 text-sm flex items-center gap-2">
                    <span className="text-gray-700">
                      {field?.label} {filter.operator.replace('_', ' ')} "{filter.value}"
                    </span>
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Results Table */}
      {showResults && (
        <div className="bg-white border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Report Results</h2>
              <p className="text-sm text-gray-600 mt-1">{reportData.length} records found</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">District</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.length > 0 ? (
                  reportData.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.district}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.role}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 text-xs font-semibold ${
                          row.status === 'Approved' ? 'bg-green-100 text-green-700' :
                          row.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                      No records found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Custom Filters</h2>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <button
                  onClick={addFilter}
                  className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Filter
                </button>

                {filters.map((filter, index) => {
                  const selectedField = formFields.find(f => f.id === filter.field);
                  return (
                    <div key={filter.id} className="border border-gray-200 p-4 bg-gray-50">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 grid grid-cols-3 gap-4">
                          {/* Field Selection */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Field</label>
                            <select
                              value={filter.field}
                              onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                            >
                              <option value="">Select field</option>
                              {formFields.map(field => (
                                <option key={field.id} value={field.id}>{field.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Operator Selection */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Operator</label>
                            <select
                              value={filter.operator}
                              onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                            >
                              {operators.map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Value Input */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Value</label>
                            {selectedField?.type === 'dropdown' ? (
                              <select
                                value={filter.value}
                                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                              >
                                <option value="">Select value</option>
                                {selectedField.options.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={selectedField?.type === 'date' ? 'date' : 'text'}
                                value={filter.value}
                                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                placeholder="Enter value"
                                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                              />
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeFilter(filter.id)}
                          className="mt-6 p-2 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {filters.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No filters added. Click "Add Filter" to create custom filters.</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;